import { ethers } from 'ethers';
import { ACTIVE_CONTRACTS } from '../../config/contract';

// Logic and Views both expose `S()` = the Storage address they were deployed with
const S_ABI = ['function S() external view returns (address)'];

export const sameAddr = (a, b) => !!a && !!b && a.toLowerCase() === b.toLowerCase();

const hasCode = async (provider, addr) => {
  try {
    return ethers.utils.isAddress(addr) && (await provider.getCode(addr)) !== '0x';
  } catch {
    return false;
  }
};

const readS = async (provider, addr) => {
  try {
    return await new ethers.Contract(addr, S_ABI, provider).S();
  } catch {
    return null;
  }
};

const safe = async (fn) => {
  try {
    return await fn();
  } catch {
    return null;
  }
};

/**
 * Checks every link between the frontend config and the deployed contracts.
 * fix: 'setLogic' = owner can fix from the panel, 'config' = update contract.js,
 *      'redeploy' = contract was deployed with the wrong Storage address
 */
export const checkWiring = async (provider, storage) => {
  const A = ACTIVE_CONTRACTS;

  const [codeStorage, codeLogic, codeViews, codeToken] = await Promise.all([
    hasCode(provider, A.storage),
    hasCode(provider, A.logic),
    hasCode(provider, A.views),
    hasCode(provider, A.token)
  ]);

  const [logicS, viewsS, storageLogic, storageToken] = await Promise.all([
    codeLogic ? readS(provider, A.logic) : null,
    codeViews ? readS(provider, A.views) : null,
    codeStorage ? safe(() => storage.logicContract()) : null,
    codeStorage ? safe(() => storage.token()) : null
  ]);

  const checks = [
    { key: 'code-storage', label: 'Storage contract deployed', ok: codeStorage, expected: A.storage, fix: 'config' },
    { key: 'code-logic', label: 'Logic contract deployed', ok: codeLogic, expected: A.logic, fix: 'config' },
    { key: 'code-views', label: 'Views contract deployed', ok: codeViews, expected: A.views, fix: 'config' },
    { key: 'code-token', label: 'Token contract deployed', ok: codeToken, expected: A.token, fix: 'config' },
    {
      key: 'storage-logic',
      label: 'Storage → Logic (logicContract)',
      ok: sameAddr(storageLogic, A.logic),
      actual: storageLogic,
      expected: A.logic,
      fix: 'setLogic'
    },
    {
      key: 'logic-storage',
      label: 'Logic → Storage (S)',
      ok: sameAddr(logicS, A.storage),
      actual: logicS,
      expected: A.storage,
      fix: 'redeploy'
    },
    {
      key: 'views-storage',
      label: 'Views → Storage (S)',
      ok: sameAddr(viewsS, A.storage),
      actual: viewsS,
      expected: A.storage,
      fix: 'redeploy'
    },
    {
      key: 'storage-token',
      label: 'Storage token',
      ok: sameAddr(storageToken, A.token),
      actual: storageToken,
      expected: A.token,
      fix: 'config'
    }
  ];

  return {
    checks,
    allOk: checks.every((c) => c.ok),
    storageLogic,
    checkedAt: Date.now()
  };
};

// Before pointing Storage at a new Logic: it must be a contract built for THIS Storage
export const validateLogicCandidate = async (provider, addr) => {
  if (!ethers.utils.isAddress(addr) || addr === ethers.constants.AddressZero) {
    return 'Enter a valid contract address';
  }
  if (!(await hasCode(provider, addr))) return 'No contract deployed at this address';
  const s = await readS(provider, addr);
  if (!s) return 'This contract has no S() - it is not a Gainforce Logic contract';
  if (!sameAddr(s, ACTIVE_CONTRACTS.storage)) {
    return `This Logic was deployed for a different Storage (${s})`;
  }
  return null;
};
